import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { financeiroApi, clientApi, catalogApi } from '../../../services/api';
import { Client, FaturaItem as FaturaItemType, Service } from '../../../types';

interface FaturaItemForm {
  id?: number;
  ordem: number;
  quantidade: number;
  discriminacao: string;
  valor_unitario: string;
  valor_total: string;
  veiculos_placas?: string;
  bm?: string;
  local_operacao?: string;
}

interface FormData {
  client: number;
  numero_fatura: string;
  data_emissao: string;
  data_vencimento: string;
  tipo_fatura: string;
  status: 'E' | 'P' | 'PP' | 'V' | 'C';
  observacoes: string;

  // Valores
  valor_servicos: string;
  valor_deducoes: string;
  base_calculo: string;
  aliquota_iss: string;
  valor_iss: string;
  valor_iss_retido: string;
  valor_irrf: string;
  valor_inss: string;
  valor_pis: string;
  valor_cofins: string;
  valor_csll: string;
  outras_retencoes: string;
  desconto_incondicionado: string;
  desconto_condicionado: string;
  valor_total: string;
  valor_pago: string;

  // Nota fiscal
  numero_nfse: string;
  codigo_verificacao: string;

  // Itens
  itens: FaturaItemForm[];
}

interface FaturaFormProps {
  mode: 'create' | 'edit';
}

export const FaturaForm: React.FC<FaturaFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Clientes
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [searchClientTerm, setSearchClientTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Serviços do catálogo
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [searchServiceTerm, setSearchServiceTerm] = useState('');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState<number | null>(null);

  const [formData, setFormData] = useState<FormData>({
    client: 0,
    numero_fatura: '',
    data_emissao: new Date().toISOString().split('T')[0],
    data_vencimento: '',
    tipo_fatura: 'FATURA DE LOCAÇÃO',
    status: 'E',
    observacoes: '',
    valor_servicos: '0,00',
    valor_deducoes: '0,00',
    base_calculo: '0,00',
    aliquota_iss: '5,00',
    valor_iss: '0,00',
    valor_iss_retido: '0,00',
    valor_irrf: '0,00',
    valor_inss: '0,00',
    valor_pis: '0,00',
    valor_cofins: '0,00',
    valor_csll: '0,00',
    outras_retencoes: '0,00',
    desconto_incondicionado: '0,00',
    desconto_condicionado: '0,00',
    valor_total: '0,00',
    valor_pago: '0,00',
    numero_nfse: '',
    codigo_verificacao: '',
    itens: []
  });

  // Busca de clientes com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchClientTerm.length >= 3) {
        loadClients(searchClientTerm);
        setShowClientDropdown(true);
      } else if (searchClientTerm.length === 0) {
        setClients([]);
        setShowClientDropdown(false);
      } else {
        setShowClientDropdown(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchClientTerm]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carregar fatura para edição
  useEffect(() => {
    if (mode === 'edit' && id) {
      loadFatura();
    }
  }, [mode, id]);

  // Recalcular valores quando itens ou impostos mudarem
  useEffect(() => {
    calcularTotais();
  }, [formData.itens, formData.valor_deducoes, formData.aliquota_iss, formData.valor_irrf,
      formData.valor_inss, formData.valor_pis, formData.valor_cofins, formData.valor_csll,
      formData.outras_retencoes, formData.desconto_incondicionado, formData.desconto_condicionado]);

  const loadClients = async (search: string) => {
    try {
      setLoadingClients(true);
      const response = await clientApi.getClients({ search, is_active: true, page_size: 50 });
      setClients(response.results || []);
    } catch (err) {
      console.error('Erro ao buscar clientes:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadServices = async (search: string) => {
    try {
      setLoadingServices(true);
      const response = await catalogApi.getServices({ search, is_active: true });
      setServices(response.results || []);
    } catch (err) {
      console.error('Erro ao buscar servicos:', err);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleSearchService = (search: string) => {
    setSearchServiceTerm(search);
    if (search.length >= 2) {
      loadServices(search);
    } else {
      setServices([]);
    }
  };

  const handleSelectService = (service: Service) => {
    if (currentItemIndex !== null) {
      // Determinar o preco (usar base_rate, daily_price ou monthly_price)
      const precoRaw = service.base_rate || service.daily_price || service.monthly_price || 0;
      const preco = Number(precoRaw) || 0;

      setFormData(prev => {
        const novosItens = [...prev.itens];
        novosItens[currentItemIndex] = {
          ...novosItens[currentItemIndex],
          discriminacao: service.name + (service.short_description ? ' - ' + service.short_description : ''),
          valor_unitario: formatValorBR(preco),
          valor_total: formatValorBR(novosItens[currentItemIndex].quantidade * preco)
        };
        return { ...prev, itens: novosItens };
      });
    }
    setShowServiceModal(false);
    setSearchServiceTerm('');
    setServices([]);
    setCurrentItemIndex(null);
  };

  const abrirBuscaServico = (index: number) => {
    setCurrentItemIndex(index);
    setShowServiceModal(true);
    loadServices(''); // Carregar todos inicialmente
  };

  const loadFatura = async () => {
    try {
      setLoading(true);
      const fatura = await financeiroApi.getFatura(Number(id));

      // Buscar nome do cliente
      if (fatura.client) {
        const clientData = await clientApi.getClient(fatura.client.toString());
        setSelectedClientName(clientData.name);
      }

      setFormData({
        client: fatura.client,
        numero_fatura: fatura.numero_fatura,
        data_emissao: fatura.data_emissao.split('T')[0],
        data_vencimento: fatura.data_vencimento.split('T')[0],
        tipo_fatura: fatura.tipo_fatura || 'FATURA DE LOCAÇÃO',
        status: fatura.status,
        observacoes: fatura.observacoes || '',
        valor_servicos: formatValorBR(fatura.valor_servicos || 0),
        valor_deducoes: formatValorBR(fatura.valor_deducoes || 0),
        base_calculo: formatValorBR(fatura.base_calculo || 0),
        aliquota_iss: formatValorBR(fatura.aliquota_iss || 5),
        valor_iss: formatValorBR(fatura.valor_iss || 0),
        valor_iss_retido: formatValorBR(fatura.valor_iss_retido || 0),
        valor_irrf: formatValorBR(fatura.valor_irrf || 0),
        valor_inss: formatValorBR(fatura.valor_inss || 0),
        valor_pis: formatValorBR(fatura.valor_pis || 0),
        valor_cofins: formatValorBR(fatura.valor_cofins || 0),
        valor_csll: formatValorBR(fatura.valor_csll || 0),
        outras_retencoes: formatValorBR(fatura.outras_retencoes || 0),
        desconto_incondicionado: formatValorBR(fatura.desconto_incondicionado || 0),
        desconto_condicionado: formatValorBR(fatura.desconto_condicionado || 0),
        valor_total: formatValorBR(fatura.valor_total),
        valor_pago: formatValorBR(fatura.valor_pago),
        numero_nfse: fatura.numero_nfse || '',
        codigo_verificacao: fatura.codigo_verificacao || '',
        itens: (fatura.itens || []).map(item => ({
          ...item,
          valor_unitario: formatValorBR(item.valor_unitario),
          valor_total: formatValorBR(item.valor_total)
        }))
      });
    } catch (err: any) {
      setError('Erro ao carregar fatura: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatValorBR = (valor: number): string => {
    return valor.toFixed(2).replace('.', ',');
  };

  const parseValorBR = (valor: string): number => {
    return parseFloat(valor.replace(/\./g, '').replace(',', '.')) || 0;
  };

  const formatValorInput = (value: string): string => {
    let cleaned = value.replace(/[^\d,]/g, '');
    const parts = cleaned.split(',');
    if (parts.length > 2) {
      cleaned = parts[0] + ',' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      cleaned = parts[0] + ',' + parts[1].substring(0, 2);
    }
    const [inteiro, decimal] = cleaned.split(',');
    const inteiroFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decimal !== undefined ? `${inteiroFormatado},${decimal}` : inteiroFormatado;
  };

  const calcularTotais = () => {
    // Calcular valor total dos serviços (soma dos itens)
    const valorServicos = formData.itens.reduce((sum, item) => {
      return sum + parseValorBR(item.valor_total);
    }, 0);

    const valorDeducoes = parseValorBR(formData.valor_deducoes);
    const baseCalculo = valorServicos - valorDeducoes;

    // Calcular ISS
    const aliquotaISS = parseValorBR(formData.aliquota_iss);
    const valorISS = (baseCalculo * aliquotaISS) / 100;

    // Total de retenções
    const totalRetencoes =
      parseValorBR(formData.valor_iss_retido) +
      parseValorBR(formData.valor_irrf) +
      parseValorBR(formData.valor_inss) +
      parseValorBR(formData.valor_pis) +
      parseValorBR(formData.valor_cofins) +
      parseValorBR(formData.valor_csll) +
      parseValorBR(formData.outras_retencoes);

    // Total de descontos
    const totalDescontos =
      parseValorBR(formData.desconto_incondicionado) +
      parseValorBR(formData.desconto_condicionado);

    // Valor total líquido
    const valorTotal = valorServicos - totalRetencoes - totalDescontos;

    // Atualizar apenas se valores mudaram (evitar loop infinito)
    setFormData(prev => {
      const newValorServicos = formatValorBR(valorServicos);
      const newBaseCalculo = formatValorBR(baseCalculo);
      const newValorISS = formatValorBR(valorISS);
      const newValorTotal = formatValorBR(valorTotal);

      if (prev.valor_servicos !== newValorServicos ||
          prev.base_calculo !== newBaseCalculo ||
          prev.valor_iss !== newValorISS ||
          prev.valor_total !== newValorTotal) {
        return {
          ...prev,
          valor_servicos: newValorServicos,
          base_calculo: newBaseCalculo,
          valor_iss: newValorISS,
          valor_total: newValorTotal
        };
      }
      return prev;
    });
  };

  const adicionarItem = () => {
    const novoItem: FaturaItemForm = {
      ordem: formData.itens.length + 1,
      quantidade: 1,
      discriminacao: '',
      valor_unitario: '0,00',
      valor_total: '0,00',
      veiculos_placas: '',
      bm: '',
      local_operacao: ''
    };
    setFormData(prev => ({ ...prev, itens: [...prev.itens, novoItem] }));
  };

  const removerItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index).map((item, i) => ({ ...item, ordem: i + 1 }))
    }));
  };

  const atualizarItem = (index: number, campo: keyof FaturaItemForm, valor: any) => {
    setFormData(prev => {
      const novosItens = [...prev.itens];
      novosItens[index] = { ...novosItens[index], [campo]: valor };

      // Recalcular valor total do item se quantidade ou valor unitário mudarem
      if (campo === 'quantidade' || campo === 'valor_unitario') {
        const qtd = campo === 'quantidade' ? parseInt(valor) : novosItens[index].quantidade;
        const valorUnit = campo === 'valor_unitario' ? parseValorBR(valor) : parseValorBR(novosItens[index].valor_unitario);
        novosItens[index].valor_total = formatValorBR(qtd * valorUnit);
      }

      return { ...prev, itens: novosItens };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'client') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const data = {
        client: formData.client,
        numero_fatura: formData.numero_fatura,
        data_emissao: formData.data_emissao,
        data_vencimento: formData.data_vencimento,
        tipo_fatura: formData.tipo_fatura,
        status: formData.status,
        observacoes: formData.observacoes || undefined,
        valor_servicos: parseValorBR(formData.valor_servicos),
        valor_deducoes: parseValorBR(formData.valor_deducoes),
        base_calculo: parseValorBR(formData.base_calculo),
        aliquota_iss: parseValorBR(formData.aliquota_iss),
        valor_iss: parseValorBR(formData.valor_iss),
        valor_iss_retido: parseValorBR(formData.valor_iss_retido),
        valor_irrf: parseValorBR(formData.valor_irrf),
        valor_inss: parseValorBR(formData.valor_inss),
        valor_pis: parseValorBR(formData.valor_pis),
        valor_cofins: parseValorBR(formData.valor_cofins),
        valor_csll: parseValorBR(formData.valor_csll),
        outras_retencoes: parseValorBR(formData.outras_retencoes),
        desconto_incondicionado: parseValorBR(formData.desconto_incondicionado),
        desconto_condicionado: parseValorBR(formData.desconto_condicionado),
        valor_total: parseValorBR(formData.valor_total),
        valor_pago: parseValorBR(formData.valor_pago),
        numero_nfse: formData.numero_nfse || undefined,
        codigo_verificacao: formData.codigo_verificacao || undefined,
        itens: formData.itens.map(item => ({
          ...item,
          valor_unitario: parseValorBR(item.valor_unitario),
          valor_total: parseValorBR(item.valor_total)
        }))
      };

      if (mode === 'create') {
        await financeiroApi.createFatura(data);
        alert('Fatura criada com sucesso!');
      } else {
        await financeiroApi.updateFatura(Number(id), data);
        alert('Fatura atualizada com sucesso!');
      }
      navigate('/financeiro/faturas');
    } catch (err: any) {
      console.error('Erro ao salvar fatura:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar fatura');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-2">
      <div className="mb-1">
        <h1 className="text-lg font-bold text-secondary-900">
          {mode === 'create' ? 'Nova Fatura' : 'Editar Fatura'}
        </h1>
        <p className="text-xs text-secondary-600">
          {mode === 'create'
            ? 'Preencha os dados para criar uma nova fatura com itens de serviço'
            : 'Atualize as informações da fatura'}
        </p>
      </div>

      {error && (
        <div className="mb-1 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-1">
        {/* Dados Básicos */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Dados Básicos</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
            {/* Cliente com busca */}
            <div className="md:col-span-4">
              <label htmlFor="client" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Cliente *
              </label>
              <div ref={clientDropdownRef} className="relative">
                <input
                  type="text"
                  placeholder="Digite para buscar cliente..."
                  value={selectedClientName || searchClientTerm}
                  onChange={(e) => {
                    setSearchClientTerm(e.target.value);
                    setSelectedClientName('');
                    setFormData(prev => ({ ...prev, client: 0 }));
                  }}
                  onFocus={() => {
                    if (clients.length > 0) {
                      setShowClientDropdown(true);
                    }
                  }}
                  className="input-field"
                  required={!selectedClientName}
                />

                {showClientDropdown && clients.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                    {clients.map((client) => (
                      <div
                        key={client.id}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, client: Number(client.id) }));
                          setSelectedClientName(client.name);
                          setSearchClientTerm('');
                          setShowClientDropdown(false);
                        }}
                      >
                        <div className="font-medium text-sm">{client.name}</div>
                        {client.cpf_cnpj && (
                          <div className="text-xs text-gray-500">{client.cpf_cnpj}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {searchClientTerm.length > 0 && searchClientTerm.length < 3
                    ? 'Digite pelo menos 3 caracteres'
                    : clients.length > 0
                    ? `${clients.length} resultado(s) encontrado(s)`
                    : selectedClientName
                    ? `Selecionado: ${selectedClientName}`
                    : 'Digite para buscar'}
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="numero_fatura" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Número da Fatura *
              </label>
              <input
                type="text"
                id="numero_fatura"
                name="numero_fatura"
                value={formData.numero_fatura}
                onChange={handleChange}
                required
                placeholder="Ex: FAT-2025-001"
                className="input-field"
              />
            </div>

            <div>
              <label htmlFor="tipo_fatura" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Tipo de Fatura
              </label>
              <input
                type="text"
                id="tipo_fatura"
                name="tipo_fatura"
                value={formData.tipo_fatura}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label htmlFor="data_emissao" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Data de Emissão *
              </label>
              <input
                type="date"
                id="data_emissao"
                name="data_emissao"
                value={formData.data_emissao}
                onChange={handleChange}
                required
                className="input-field"
              />
            </div>

            <div>
              <label htmlFor="data_vencimento" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Data de Vencimento *
              </label>
              <input
                type="date"
                id="data_vencimento"
                name="data_vencimento"
                value={formData.data_vencimento}
                onChange={handleChange}
                required
                className="input-field"
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Status *
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="input-field"
              >
                <option value="E">Emitida</option>
                <option value="P">Paga</option>
                <option value="PP">Parcialmente Paga</option>
                <option value="V">Vencida</option>
                <option value="C">Cancelada</option>
              </select>
            </div>
          </div>
        </div>

        {/* Itens da Fatura */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-0.5">
            <h3 className="text-xs font-semibold text-secondary-900">Itens/Serviços da Fatura</h3>
            <button
              type="button"
              onClick={adicionarItem}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              + Adicionar Item
            </button>
          </div>

          {formData.itens.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-xs">
              Nenhum item adicionado. Clique em "Adicionar Item" para começar.
            </div>
          ) : (
            <div className="space-y-1">
              {formData.itens.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-1.5 bg-gray-50">
                  <div className="flex justify-between items-start mb-0.5">
                    <span className="text-xs font-medium text-secondary-700">Item #{index + 1}</span>
                    <button
                      type="button"
                      onClick={() => removerItem(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remover
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-6 gap-1">
                    <div className="md:col-span-4">
                      <div className="flex justify-between items-center mb-0.5">
                        <label className="block text-xs font-medium text-secondary-700">
                          Discriminacao/Descricao do Servico *
                        </label>
                        <button
                          type="button"
                          onClick={() => abrirBuscaServico(index)}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          Buscar do Catalogo
                        </button>
                      </div>
                      <textarea
                        value={item.discriminacao}
                        onChange={(e) => atualizarItem(index, 'discriminacao', e.target.value)}
                        rows={2}
                        required
                        className="input-field"
                        placeholder="Descricao detalhada do servico prestado..."
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                        Quantidade *
                      </label>
                      <input
                        type="number"
                        value={item.quantidade}
                        onChange={(e) => atualizarItem(index, 'quantidade', parseInt(e.target.value) || 1)}
                        min="1"
                        required
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                        Valor Unitário *
                      </label>
                      <input
                        type="text"
                        value={item.valor_unitario}
                        onChange={(e) => atualizarItem(index, 'valor_unitario', formatValorInput(e.target.value))}
                        required
                        placeholder="0,00"
                        className="input-field"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                        Veículos/Placas
                      </label>
                      <input
                        type="text"
                        value={item.veiculos_placas || ''}
                        onChange={(e) => atualizarItem(index, 'veiculos_placas', e.target.value)}
                        className="input-field"
                        placeholder="Ex: ABC-1234, DEF-5678"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                        BM
                      </label>
                      <input
                        type="text"
                        value={item.bm || ''}
                        onChange={(e) => atualizarItem(index, 'bm', e.target.value)}
                        className="input-field"
                        placeholder="Boletim de Medição"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                        Local da Operação
                      </label>
                      <input
                        type="text"
                        value={item.local_operacao || ''}
                        onChange={(e) => atualizarItem(index, 'local_operacao', e.target.value)}
                        className="input-field"
                        placeholder="Local onde o serviço foi prestado"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-secondary-700 mb-0.5">
                        Valor Total
                      </label>
                      <input
                        type="text"
                        value={item.valor_total}
                        readOnly
                        className="input-field bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Impostos e Retenções */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Impostos e Retenções</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Valor Serviços</label>
              <input
                type="text"
                value={formData.valor_servicos}
                readOnly
                className="input-field bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Deduções</label>
              <input
                type="text"
                name="valor_deducoes"
                value={formData.valor_deducoes}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_deducoes: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Base de Cálculo</label>
              <input
                type="text"
                value={formData.base_calculo}
                readOnly
                className="input-field bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Alíquota ISS (%)</label>
              <input
                type="text"
                name="aliquota_iss"
                value={formData.aliquota_iss}
                onChange={(e) => setFormData(prev => ({ ...prev, aliquota_iss: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">ISS Calculado</label>
              <input
                type="text"
                value={formData.valor_iss}
                readOnly
                className="input-field bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">ISS Retido</label>
              <input
                type="text"
                name="valor_iss_retido"
                value={formData.valor_iss_retido}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_iss_retido: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">IRRF</label>
              <input
                type="text"
                name="valor_irrf"
                value={formData.valor_irrf}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_irrf: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">INSS</label>
              <input
                type="text"
                name="valor_inss"
                value={formData.valor_inss}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_inss: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">PIS</label>
              <input
                type="text"
                name="valor_pis"
                value={formData.valor_pis}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_pis: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">COFINS</label>
              <input
                type="text"
                name="valor_cofins"
                value={formData.valor_cofins}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_cofins: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">CSLL</label>
              <input
                type="text"
                name="valor_csll"
                value={formData.valor_csll}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_csll: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Outras Retenções</label>
              <input
                type="text"
                name="outras_retencoes"
                value={formData.outras_retencoes}
                onChange={(e) => setFormData(prev => ({ ...prev, outras_retencoes: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Desconto Incondicionado</label>
              <input
                type="text"
                name="desconto_incondicionado"
                value={formData.desconto_incondicionado}
                onChange={(e) => setFormData(prev => ({ ...prev, desconto_incondicionado: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Desconto Condicionado</label>
              <input
                type="text"
                name="desconto_condicionado"
                value={formData.desconto_condicionado}
                onChange={(e) => setFormData(prev => ({ ...prev, desconto_condicionado: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Valor Total Líquido</label>
              <input
                type="text"
                value={formData.valor_total}
                readOnly
                className="input-field bg-green-50 font-bold text-green-700"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-secondary-700 mb-0.5">Valor Pago</label>
              <input
                type="text"
                name="valor_pago"
                value={formData.valor_pago}
                onChange={(e) => setFormData(prev => ({ ...prev, valor_pago: formatValorInput(e.target.value) }))}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* NF-Se e Observações */}
        <div className="bg-white p-1.5 rounded-md shadow-sm border border-gray-200">
          <h3 className="text-xs font-semibold text-secondary-900 mb-0.5">Nota Fiscal e Observações</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
            <div>
              <label htmlFor="numero_nfse" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Número NFS-e
              </label>
              <input
                type="text"
                id="numero_nfse"
                name="numero_nfse"
                value={formData.numero_nfse}
                onChange={handleChange}
                className="input-field"
                placeholder="Número da Nota Fiscal de Serviço Eletrônica"
              />
            </div>

            <div>
              <label htmlFor="codigo_verificacao" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Código de Verificação
              </label>
              <input
                type="text"
                id="codigo_verificacao"
                name="codigo_verificacao"
                value={formData.codigo_verificacao}
                onChange={handleChange}
                className="input-field"
                placeholder="Código de verificação da NFS-e"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="observacoes" className="block text-xs font-medium text-secondary-700 mb-0.5">
                Observações
              </label>
              <textarea
                id="observacoes"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                rows={4}
                placeholder="Informações adicionais sobre a fatura..."
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end space-x-2 pt-1">
          <button
            type="button"
            onClick={() => navigate('/financeiro/faturas')}
            className="px-3 py-1.5 text-xs font-medium text-secondary-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || formData.client === 0}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:bg-gray-400"
          >
            {saving ? 'Salvando...' : mode === 'create' ? 'Criar Fatura' : 'Salvar Alteracoes'}
          </button>
        </div>
      </form>

      {/* Modal de Busca de Servicos do Catalogo */}
      {showServiceModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowServiceModal(false)}></div>

            <div className="relative inline-block w-full max-w-2xl p-4 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Buscar Servico do Catalogo</h3>
                <button
                  type="button"
                  onClick={() => setShowServiceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  X
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Digite para buscar servico..."
                  value={searchServiceTerm}
                  onChange={(e) => handleSearchService(e.target.value)}
                  className="input-field w-full"
                  autoFocus
                />
              </div>

              <div className="max-h-96 overflow-y-auto">
                {loadingServices ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Carregando...</p>
                  </div>
                ) : services.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    {searchServiceTerm.length >= 2 ? 'Nenhum servico encontrado' : 'Digite para buscar'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {services.map((service) => (
                      <div
                        key={service.id}
                        className="p-3 border border-gray-200 rounded-md hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => handleSelectService(service)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{service.name}</div>
                            {service.short_description && (
                              <div className="text-xs text-gray-500 mt-0.5">{service.short_description}</div>
                            )}
                            {service.category_name && (
                              <div className="text-xs text-blue-600 mt-1">Categoria: {service.category_name}</div>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            {service.base_rate ? (
                              <div className="text-sm font-medium text-green-600">
                                R$ {Number(service.base_rate).toFixed(2).replace('.', ',')}
                              </div>
                            ) : service.daily_price ? (
                              <div className="text-sm font-medium text-green-600">
                                R$ {Number(service.daily_price).toFixed(2).replace('.', ',')}/dia
                              </div>
                            ) : service.monthly_price ? (
                              <div className="text-sm font-medium text-green-600">
                                R$ {Number(service.monthly_price).toFixed(2).replace('.', ',')}/mes
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">Sem preco</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowServiceModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
